import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';

/**
 * @typedef {Object} AxShopTheLookRefs
 * @property {HTMLButtonElement[]} [dots]
 * @property {HTMLAnchorElement[]} [cards]
 * @property {HTMLElement} [stage]
 */

/**
 * Ax Shop the Look — interactive product pins over a background image.
 *
 * Click a dot to reveal its product card under the dot (desktop) or at the
 * bottom of the stage (mobile). Only one card visible at a time. Re-clicking
 * the active dot closes the card. On desktop, the card is clamped horizontally
 * so it never overflows the stage when a dot is near the edge.
 *
 * @extends {Component<AxShopTheLookRefs>}
 */
class AxShopTheLook extends Component {
  #activeIndex = 0;
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#sync();

    this.#resizeObserver = new ResizeObserver(() => this.#clampActiveCard());
    if (this.refs.stage) this.#resizeObserver.observe(this.refs.stage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Toggle the active product pin. The component framework passes the parsed
   * arg first, then the event (see localization-form for the same pattern).
   * @param {number} index
   * @param {Event} _event
   */
  handleDotClick(index, _event) {
    this.#activeIndex = this.#activeIndex === index ? -1 : index;
    this.#sync();
  }

  #sync() {
    const { dots, cards } = this.refs;
    if (!dots || !cards) return;

    for (let i = 0; i < dots.length; i++) {
      dots[i].setAttribute('aria-expanded', String(i === this.#activeIndex));
    }

    for (let i = 0; i < cards.length; i++) {
      cards[i].hidden = i !== this.#activeIndex;
    }

    this.#clampActiveCard();
  }

  /**
   * Desktop only — shift the active card horizontally so it stays inside the
   * stage when a dot is near the left/right edge. Mobile uses a bottom-pinned
   * layout, so no clamp needed.
   */
  #clampActiveCard() {
    if (this.#activeIndex < 0) return;
    if (!mediaQueryLarge.matches) return;

    const card = this.refs.cards?.[this.#activeIndex];
    const stage = this.refs.stage;
    if (!card || !stage) return;

    card.style.removeProperty('--ax-stl-card-offset');

    // Force a layout read AFTER the offset reset
    const cardRect = card.getBoundingClientRect();
    const stageRect = stage.getBoundingClientRect();
    const margin = 8;

    let offset = 0;
    if (cardRect.left < stageRect.left + margin) {
      offset = stageRect.left + margin - cardRect.left;
    } else if (cardRect.right > stageRect.right - margin) {
      offset = stageRect.right - margin - cardRect.right;
    }

    if (offset !== 0) {
      card.style.setProperty('--ax-stl-card-offset', `${offset}px`);
    }
  }
}

customElements.define('ax-shop-the-look', AxShopTheLook);
